import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, str, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { recurrenceSchema, scheduleNextField, type Recurrence, type SaveRecurrenceDefinition, type ScheduleNext } from './types';

// POST /recurrence_definitions — create. Backend:
// `RecurrenceDefinitionService.create` returns the `INSERT … RETURNING` row with
// `zone_name` echoed from the request (not read from the DB) + the shared
// `recurrences` / `folder_path` serializers.
//
// `CreatedRecurrenceDefinition` is its OWN type + schema, even though it matches
// the read shape today: it is produced by a different backend statement
// (equal-by-coincidence, not identity — see `../../README.md`). Structural
// typing lets it flow wherever a `RecurrenceDefinition` is expected downstream.
export interface CreatedRecurrenceDefinition {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string | null;
  readonly resubmit_on_error: boolean;
  readonly schedule_next: ScheduleNext;
  readonly recurrences: readonly Recurrence[];
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

const createdSchema = object<CreatedRecurrenceDefinition>({
  recurrence_definition_id: num(REJECT),
  name: str(),
  description: nullable(str()),
  zone_name: nullable(str()),
  resubmit_on_error: bool(),
  schedule_next: scheduleNextField,
  recurrences: array(recurrenceSchema),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  created_at: nullable(str()),
  updated_at: nullable(str()),
});

/**
 * POST `/recurrence_definitions`. Returns the created record.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const postRecurrenceDefinition = async (save: SaveRecurrenceDefinition): Promise<Received<CreatedRecurrenceDefinition>> => {
  const res = await api.post('/recurrence_definitions', save);
  return readResponse(res.data, { schema: createdSchema, source: 'postRecurrenceDefinition' });
};
